import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface InputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'size'> {
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  leadingIcon?: ReactNode;
  /** Rendered at the trailing edge — e.g. a password reveal toggle. */
  trailingSlot?: ReactNode;
}

const SIZES = {
  sm: 'h-8 text-xs',
  md: 'h-9 text-sm',
  lg: 'h-11 text-base',
} as const;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, size = 'md', leadingIcon, trailingSlot, disabled, ...rest },
  ref,
) {
  const field = (
    <input
      ref={ref}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      className={cn(
        'peer w-full rounded-sm bg-surface-raised text-text-primary',
        'border border-border-default',
        'transition-colors duration-[--duration-micro]',
        'hover:border-border-strong',
        'focus:border-primary focus:outline-none',
        'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-disabled',
        'aria-[invalid]:border-danger',
        SIZES[size],
        leadingIcon ? 'pl-9' : 'pl-3',
        trailingSlot ? 'pr-10' : 'pr-3',
        className,
      )}
      {...rest}
    />
  );

  if (!leadingIcon && !trailingSlot) return field;

  return (
    <div className="relative flex items-center">
      {leadingIcon ? (
        <span
          className="pointer-events-none absolute left-3 flex text-text-tertiary [&_svg]:size-4"
          aria-hidden="true"
        >
          {leadingIcon}
        </span>
      ) : null}
      {field}
      {trailingSlot ? (
        <span className="absolute right-1.5 flex items-center">{trailingSlot}</span>
      ) : null}
    </div>
  );
});
